import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { BOOKMARK_TARGET_COMPONENT, type TriggerObservation } from '@sfpbxtestbot/contracts';
import { RunForm } from '../components/RunForm.js';
import { RunTimeline } from '../components/RunTimeline.js';
import { LogViewer } from '../components/LogViewer.js';
import { ScreenshotGallery } from '../components/Evidence.js';
import {
  Button,
  Callout,
  Card,
  EmptyState,
  PageHeader,
  Stat,
  StatusBadge,
  formatDuration,
} from '../components/primitives.js';
import { api } from '../lib/api.js';
import { useRunStream } from '../lib/useRunStream.js';

const KIND_EXPLAINER: Record<TriggerObservation['kind'], string> = {
  Bookmark: 'Should appear in Live Bookmarks',
  ActionEngine: 'Should appear in Live Bookmarks',
  CallAssist: 'Should appear in Call Assist',
  ChecklistItem: 'Should tick off in the in-call checklist',
};

/**
 * Live Call Test.
 *
 * The bot reads the tenant's bookmarks and checklist first, then builds a
 * conversation that deliberately says every trigger phrase. That is what makes
 * the result meaningful: each row below is a phrase we know was spoken, and
 * whether Salesforce reacted to it.
 */
export function LiveCallPage(): ReactNode {
  const [runId, setRunId] = useState<string>();
  const stream = useRunStream(runId);
  const [stopping, setStopping] = useState(false);

  const inProgress = stream.run?.outcome === 'running' || stream.run?.outcome === 'pending';
  const observed = stream.triggers.filter((trigger) => trigger.observed).length;

  async function stop(): Promise<void> {
    if (!runId) return;
    setStopping(true);
    try {
      await api.stopRun(runId);
    } finally {
      setStopping(false);
    }
  }

  return (
    <div className="stack">
      <PageHeader
        title="Live call test"
        lede="Place a real call from the softphone, hold a conversation that plants every trigger phrase the tenant has configured, and check that each component reacts."
        actions={
          stream.run ? (
            <>
              <StatusBadge outcome={stream.run.outcome} />
              {inProgress ? (
                <Button variant="danger" size="small" busy={stopping} onClick={() => void stop()}>
                  Hang up and stop
                </Button>
              ) : null}
              <Link className="button button--ghost button--small" to={`/runs/${stream.run.id}`}>
                Full report
              </Link>
            </>
          ) : null
        }
      />

      {!runId ? (
        <>
          <Callout tone="info">
            <div>
              This needs an org that is already configured. Tick the in-call features this run
              should prove. A call-only run skips the Admin Portal and the voice bot. Testing Live
              Bookmarks or Call Assist still needs an Admin Portal token so the tenant&rsquo;s v2
              bookmarks can be read; the voice bot needs a Google service account. Both live in
              Settings.
            </div>
          </Callout>

          <RunForm
            stages={['live-call']}
            show={{ call: true }}
            submitLabel="Place the test call"
            onStarted={(run) => setRunId(run.id)}
          />

          <Card title="What gets checked during the call">
            <ul className="stack stack--tight" style={{ paddingLeft: 20, margin: 0, fontSize: 14 }}>
              <li>The softphone registers and the call connects. That is always checked.</li>
              <li>Live Transcript, Live Bookmarks, Call Assist, Question Assist, RocketForm and Checklist are checked only when you tick them above.</li>
              <li>Bookmarks and checklist phrases come from the v2 lists the PBX backend uses when it processes the call.</li>
              <li>Unticked features are skipped, including the Admin Portal lookup they would have needed.</li>
            </ul>
          </Card>
        </>
      ) : (
        <>
          {stream.run?.reason ? (
            <Callout tone={stream.run.outcome === 'failed' ? 'danger' : 'warning'}>
              {stream.run.reason}
            </Callout>
          ) : null}

          <Card>
            <div className="row" style={{ gap: 40 }}>
              <Stat value={stream.transcript.length} label="Transcript segments" />
              <Stat
                value={`${observed}/${stream.triggers.length}`}
                label="Triggers seen"
                tone={
                  stream.triggers.length > 0 && observed === stream.triggers.length
                    ? 'passed'
                    : observed < stream.triggers.length
                      ? 'warning'
                      : undefined
                }
              />
              <Stat value={formatDuration(stream.run?.durationMs)} label="Call length" />
            </div>
          </Card>

          <div className="grid grid--2">
            <Card title="Live transcript" subtitle="Mirrored from the component inside Salesforce.">
              {stream.transcript.length === 0 ? (
                <EmptyState
                  title="Nothing said yet"
                  body="Segments appear here as the conversation is transcribed."
                />
              ) : (
                <div className="transcript">
                  {stream.transcript.map((segment, index) => (
                    <div key={`${segment.at}-${index}`} className={`bubble bubble--${segment.speaker}`}>
                      {segment.speaker !== 'system' ? (
                        <div className="bubble__speaker">{segment.speaker}</div>
                      ) : null}
                      {segment.text}
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card
              title="Trigger tracker"
              subtitle="Each phrase the bot said on purpose, and whether the component reacted."
            >
              {stream.triggers.length === 0 ? (
                <EmptyState
                  title="No triggers planted yet"
                  body="The bot reads the tenant's bookmarks and checklist before the call, then works each phrase into the conversation."
                />
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Phrase</th>
                      <th>Expected</th>
                      <th>Reacted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stream.triggers.map((trigger) => (
                      <tr key={trigger.triggerId}>
                        <td>{trigger.phrase}</td>
                        <td className="muted" style={{ fontSize: 13 }}>
                          {KIND_EXPLAINER[trigger.kind]}
                          {trigger.kind !== 'ChecklistItem'
                            ? ` (${BOOKMARK_TARGET_COMPONENT[trigger.kind]})`
                            : ''}
                        </td>
                        <td>
                          <StatusBadge
                            outcome={trigger.observed ? 'passed' : 'failed'}
                            label={
                              trigger.observed
                                ? trigger.latencyMs != null
                                  ? `after ${formatDuration(trigger.latencyMs)}`
                                  : 'Yes'
                                : 'No'
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          </div>

          <Card
            title="Progress"
            actions={
              !inProgress ? (
                <Button size="small" onClick={() => setRunId(undefined)}>
                  Run another call
                </Button>
              ) : null
            }
          >
            <RunTimeline runId={runId} steps={stream.run?.steps ?? []} />
          </Card>

          <Card title="Screenshots">
            <ScreenshotGallery
              runId={runId}
              evidence={(stream.run?.steps ?? []).flatMap((step) => step.evidence)}
            />
          </Card>

          <Card title="Output">
            <LogViewer logs={stream.logs} />
          </Card>
        </>
      )}
    </div>
  );
}
